import moment from "moment";
import { DATE_FORMAT } from "../../constants/dateConstants";

/**
 * Column widths for the report table.
 *
 * ── Why widths are explicit now ──
 * Report cells used to wrap: a long value - a customer name, an address - stacked
 * onto two or three lines and took its row's height with it, so a twenty-row table
 * had twenty different row heights. Cells are now clipped to one line with a
 * trailing "…" instead (ReportGrid sets `ellipsis` per column), which keeps every
 * row the same height but hides the tail of a long value. The way back to it is
 * the column width: drag the right edge of a header, the way Excel does.
 *
 * That only works if every column has a width to drag FROM, so a width is resolved
 * for each one, in this order:
 *
 *   1. a width the reader has dragged this session  (ReportGrid's own state)
 *   2. `width` saved on the field by the editor      (availableFields[].width)
 *   3. the auto-fit below
 *
 * ── Auto-fit ──
 * Computed from the text, not measured from the DOM. Measuring would mean laying
 * the table out once to read the widths back and then re-rendering with them
 * pinned, which flickers; and the numbers would drift with the font. This is
 * deterministic, runs before first paint and costs one pass over a sample of rows.
 *
 * It is a character count scaled to pixels, which is approximate by nature - the
 * font is proportional, so "IIII" and "WWWW" are both four characters. That is
 * why the result is clamped at both ends: near enough that a Name column opens
 * wide and a Qty column opens narrow, and the reader adjusts from there.
 *
 * PX_PER_CHAR / CELL_CHROME_PX come from the formula this replaces - the
 * `headerMinWidth` min-width hack in ReportGrid (`length * 8 + 34`), which was
 * tuned against this table's 13px body / 12px header type and its 14px cell
 * padding. Same numbers, applied to the content as well as to the heading.
 */

/* The floor for a drag. Low enough to tuck a column out of the way, high enough
   that it cannot be lost altogether and left un-grabbable. */
export const MIN_COLUMN_WIDTH = 60;

/* Auto-fit stays inside these. The floor keeps a column of "0"s from collapsing
   to nothing; the ceiling stops one long free-text column from owning the table
   (it gets clipped, and the reader widens it if they care). */
export const AUTO_FIT_MIN = 96;
export const AUTO_FIT_MAX = 340;

/* Enough rows to see what the column holds without walking a 10,000-row report.
   Matches FIELD_SAMPLE_ROWS in redux/editorSlice.js, which samples for the same
   reason (a column's type cannot be read off row 0 alone). */
export const WIDTH_SAMPLE_ROWS = 20;

/* Roughly the widest a character runs at this table's type - 13px in the body,
   12px bold in the heading. An over-estimate on purpose: the font is
   proportional, so guessing high means a column is occasionally wider than it
   needed to be, while guessing low means text is clipped at the width this very
   function chose for it. */
export const PX_PER_CHAR = 8;

/* What a cell spends on something other than text.
   MUST match the horizontal padding in reportGrid.css (`padding: 10px 8px` on the
   header, body and summary cell - 8px each side). If that padding changes, this
   changes with it, or every column goes back to being sized for a box it does not
   have. The test in ./columnWidths.test.js fails if the two drift apart. */
export const CELL_PADDING_PX = 16;

/* ── THE NARROW-TABLE INDENT ──
   Extra left padding given to the columns of a one- or two-column table, on top
   of CELL_PADDING_PX. A report with two columns leaves most of the card empty,
   and with every column left-aligned the little content there is ends up pinned
   against the card's left edge with a vast blank to its right - the table reads
   as having fallen over to one side rather than as being laid out. Pushing the
   text in off the edge gives it a margin to sit against.

   Only applied below INDENT_MAX_COLUMNS: a table with enough columns to fill its
   width has no empty space to balance and would just lose room to the padding.

   MUST match `padding-left` on .rg-col-indent in reportGrid.css (that rule sets
   the TOTAL, so it reads CELL_PADDING_PX / 2 + this = 8 + 24 = 32px). It is added
   to the resolved width in ReportGrid, so an indented column keeps the same space
   for its text as it had before and nothing starts truncating early. */
export const INDENT_PADDING_PX = 24;

/* A table with more columns than this fills its own width, so it is left alone. */
export const INDENT_MAX_COLUMNS = 2;

/* How much wider a heading runs once the view pages set it in caps. Caps average
   noticeably wider than mixed case because the lower-case glyphs are the narrow
   ones, and the character count here cannot see the difference. */
export const HEADING_CAPS_FACTOR = 1.1;

/* Slack on top, so a heading sized to fit is not sitting exactly on the boundary
   - the estimate above is approximate, and `text-overflow` needs somewhere to
   put the "…" if it turns out to be a character short. */
const ELLIPSIS_ALLOWANCE_PX = 6;

/* 34px. The clipping box inside a cell gets `max-width: 100%`, which resolves
   against the content box - so of this, CELL_PADDING_PX goes to the cell and the
   allowance is what the text actually has spare.

   Nothing else may be subtracted from that box in CSS. The heading's box was for
   a while also reserving 9px to clear the resize grip, which put it 3px BELOW
   what this constant promises, and headings truncated at their own auto-fit
   width. The grip is absolutely positioned inside the padding, so it needs no
   reserve at all. See .rg-th-label in reportGrid.css. */
const CELL_CHROME_PX = CELL_PADDING_PX + ELLIPSIS_ALLOWANCE_PX;

export function charsToPx(chars) {
  return Math.round(Number(chars || 0) * PX_PER_CHAR + CELL_CHROME_PX);
}

export function clampAutoFit(px) {
  return Math.min(AUTO_FIT_MAX, Math.max(AUTO_FIT_MIN, Math.round(px)));
}

/* A field holds an array when it carries a nested table - see getNestedView in
   ReportGrid. Those columns are never clipped and never auto-fitted down: a table
   squeezed into 96px is unreadable. */
export function isArrayField(field, rows, dataIndex) {
  if (field && field.dataType === "array") return true;
  const sample = (rows || []).slice(0, WIDTH_SAMPLE_ROWS);
  return sample.some((row) => Array.isArray(row && row[dataIndex]));
}

/**
 * How many characters a cell actually SHOWS - which is not the length of the
 * stored value. ReportGrid formats dates through moment and floats through
 * toFixed before rendering, so a raw ISO timestamp of 24 characters may be drawn
 * as 10. Auto-fitting to the raw value would leave every date column far too wide.
 */
export function cellTextLength(value, field) {
  if (value === null || value === undefined) return 0;

  const dataType = field && field.dataType;

  if (dataType === "date") {
    const formatted = moment(value).format((field && field.format) || DATE_FORMAT);
    return String(formatted).length;
  }

  if (dataType === "float") {
    const decimals = field && field.decimalPoints ? field.decimalPoints : 2;
    const asNumber = Number(value);
    // A non-numeric value in a float column renders as "NaN" rather than throwing.
    return String(Number.isNaN(asNumber) ? "NaN" : asNumber.toFixed(decimals)).length;
  }

  if (typeof value === "boolean") return String(value).length;

  if (Array.isArray(value) || (typeof value === "object" && value !== null)) {
    // Not a text cell at all - handled by the array branch in autoFitWidth.
    return 0;
  }

  return String(value).length;
}

/**
 * The width a column opens at when nobody has set one.
 *
 * Takes the widest of the heading and a sample of the values, so a column whose
 * heading is short but whose values are long ("Name" over full names) opens wide,
 * and a column whose heading is long but whose values are short ("Is Discounted"
 * over "true") opens wide enough to read its own heading. Sizing on the heading
 * alone got the second case right and the first case wrong.
 */
export function autoFitWidth({ title, dataIndex, rows, field }) {
  if (isArrayField(field, rows, dataIndex)) return AUTO_FIT_MAX;

  /* HEADING_CAPS_FACTOR: the view pages render headings in caps
     (`text-transform: uppercase` on .board-datatable-container, see
     reportGrid.css), and caps are wider than the mixed case counted here -
     lower-case letters carry the narrow glyphs. Widths are worked out and saved
     in the EDITOR, where headings are drawn mixed case, so without this a
     heading that fitted while it was being built would come out truncated on the
     published report. Applied to the heading only; values are not transformed. */
  const headingChars =
    String(title === null || title === undefined ? "" : title).length * HEADING_CAPS_FACTOR;

  let valueChars = 0;
  const sample = (rows || []).slice(0, WIDTH_SAMPLE_ROWS);
  for (let i = 0; i < sample.length; i++) {
    const row = sample[i];
    const length = cellTextLength(row ? row[dataIndex] : undefined, field);
    if (length > valueChars) valueChars = length;
  }

  return clampAutoFit(charsToPx(Math.max(headingChars, valueChars)));
}

/**
 * The width to draw a column at: a live drag wins, then the editor's saved width,
 * then auto-fit. Kept here rather than in the component so the precedence is
 * stated once and can be unit-tested.
 */
export function resolveColumnWidth({ title, dataIndex, rows, field, draggedWidths }) {
  const dragged = draggedWidths && draggedWidths[dataIndex];
  if (Number(dragged) > 0) return Math.max(MIN_COLUMN_WIDTH, Math.round(Number(dragged)));

  const saved = field && field.width;
  if (Number(saved) > 0) return Math.max(MIN_COLUMN_WIDTH, Math.round(Number(saved)));

  return autoFitWidth({ title, dataIndex, rows, field });
}
